"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Building2, 
  ShieldCheck, 
  Trash2, 
  Mail, 
  CreditCard, 
  Settings2, 
  Percent, 
  User, 
  CheckCircle2, 
  Landmark, 
  Smartphone, 
  Zap, 
  Lock, 
  Bell, 
  Sliders, 
  Save, 
  Check, 
  AlertCircle,
  HelpCircle,
  ShieldAlert,
  Plus
} from "lucide-react";
import { AddTeamMemberModal } from "@/components/modals/add-team-member-modal";
import { cn } from "@/lib/utils";

export default function SettingsPage() {
  const [requireApproval, setRequireApproval] = useState(true);
  const [approvalThreshold, setApprovalThreshold] = useState("1000000");
  const [feePreference, setFeePreference] = useState<"charge_me" | "charge_customer" | "charge_percentage">("charge_me");
  const [merchantFeePercent, setMerchantFeePercent] = useState<number | "">(50);
  const [isSaved, setIsSaved] = useState(false);

  // Active Payment Channels State
  const [channels, setChannels] = useState({
    card: true,
    transfer: true,
    opay: true,
    palmpay: true,
    mobile_money: true,
    ussd: true,
  });

  const handleSave = () => {
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2500);
  };

  const toggleChannel = (key: keyof typeof channels) => {
    setChannels(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Business Settings</h1>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px] font-bold uppercase">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Tier 3 Verified
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Configure your corporate profile, fee splitting rules, payment channels, and security approvals.
          </p>
        </div>

        <Button 
          onClick={handleSave}
          className="bg-primary text-primary-foreground font-bold text-xs h-10 px-5 shadow-sm transition-all"
        >
          {isSaved ? (
            <>
              <Check className="mr-2 h-4 w-4 text-emerald-400" /> Changes Saved!
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" /> Save Configuration
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="profile" className="w-full space-y-6">
        <TabsList className="h-11 w-full justify-start gap-2 bg-muted/40 p-1 rounded-xl border border-border">
          <TabsTrigger value="profile" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Building2 className="h-3.5 w-3.5 mr-2" /> Business & Profile
          </TabsTrigger>
          <TabsTrigger value="fees" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Percent className="h-3.5 w-3.5 mr-2" /> Fee Rules & Channels
          </TabsTrigger>
          <TabsTrigger value="security" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <ShieldCheck className="h-3.5 w-3.5 mr-2" /> Security & Approvals
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: BUSINESS & PROFILE */}
        <TabsContent value="profile" className="space-y-6 m-0">
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Left: Corporate Profile (Read-Only / Locked) */}
            <Card className="lg:col-span-2 border shadow-sm">
              <CardHeader className="py-4 px-5 border-b bg-muted/10 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-base border border-primary/20">
                    PT
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-sm font-bold">Registered Corporate Profile</CardTitle>
                      <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-[9px] font-bold flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> Read-Only
                      </Badge>
                    </div>
                    <CardDescription className="text-[11px]">Official company identification details verified by CAC & CBN.</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px] font-bold">RC-10293847</Badge>
              </CardHeader>
              <CardContent className="p-5 space-y-5">
                {/* Compliance Notice */}
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-2.5 text-xs text-amber-700 dark:text-amber-400">
                  <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                  <p className="text-[11px] leading-relaxed">
                    <strong>Locked for Regulatory Compliance:</strong> Registered corporate profile details are legally bound to your CAC incorporation records. To request changes to your company name, registration number, or official address, please submit a corporate resolution to our Compliance Desk.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-muted-foreground flex items-center justify-between">
                      Legal Business Name
                      <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verified</span>
                    </Label>
                    <div className="h-9 px-3 rounded-lg border bg-muted/30 text-xs font-medium text-foreground flex items-center select-all">
                      PayTaara Technologies Ltd
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-muted-foreground flex items-center justify-between">
                      Registration Number (CAC / TIN)
                      <span className="text-[10px] text-muted-foreground font-mono">Official RC</span>
                    </Label>
                    <div className="h-9 px-3 rounded-lg border bg-muted/30 text-xs font-mono font-medium text-foreground flex items-center select-all">
                      RC-10293847
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-muted-foreground">Official Support Email</Label>
                    <div className="h-9 px-3 rounded-lg border bg-muted/30 text-xs font-medium text-foreground flex items-center select-all">
                      support@paytaara.com
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-muted-foreground">Support Phone Number</Label>
                    <div className="h-9 px-3 rounded-lg border bg-muted/30 text-xs font-mono font-medium text-foreground flex items-center select-all">
                      +234 1 234 5678
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-muted-foreground">Head Office Registered Address</Label>
                  <div className="h-9 px-3 rounded-lg border bg-muted/30 text-xs font-medium text-foreground flex items-center select-all">
                    123 Financial Avenue, Victoria Island, Lagos, Nigeria
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 pt-1 border-t">
                  <div className="space-y-1">
                    <span className="text-[11px] text-muted-foreground">Incorporation Type</span>
                    <p className="text-xs font-semibold text-foreground">Private Limited Company (LTD)</p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[11px] text-muted-foreground">Tax Identification Number (TIN)</span>
                    <p className="text-xs font-mono font-semibold text-foreground">23948201-0001</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Right: Personal Account Info */}
            <Card className="border shadow-sm flex flex-col">
              <CardHeader className="py-4 px-5 border-b bg-muted/10">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-bold">Administrator Identity</CardTitle>
                </div>
                <CardDescription className="text-[11px]">Primary account owner details.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4 flex-1">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/20 border">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center border border-primary/20 text-xs">
                    JD
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">John Doe</p>
                    <p className="text-[11px] text-muted-foreground">john.doe@example.com</p>
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex justify-between items-center text-xs border-b pb-2">
                    <span className="text-muted-foreground">User Role</span>
                    <span className="font-bold text-foreground">Super Administrator</span>
                  </div>
                  <div className="flex justify-between items-center text-xs border-b pb-2">
                    <span className="text-muted-foreground">Phone</span>
                    <span className="font-mono text-foreground">+234 801 234 5678</span>
                  </div>
                  <div className="flex justify-between items-center text-xs border-b pb-2">
                    <span className="text-muted-foreground">Account Status</span>
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border-none text-[10px]">Verified</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 3: FEE RULES & PAYMENT CHANNELS */}
        <TabsContent value="fees" className="space-y-6 m-0">
          
          {/* Fee Bearer Preference Cards */}
          <Card className="border shadow-sm">
            <CardHeader className="py-4 px-5 border-b bg-muted/10">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-primary" />
                <CardTitle className="text-sm font-bold">Transaction Fee Allocation Rules</CardTitle>
              </div>
              <CardDescription className="text-[11px]">
                Choose who absorbs processing fees when customers pay your business.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="p-5 space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                
                {/* Option 1: Charge Me */}
                <div 
                  onClick={() => setFeePreference("charge_me")}
                  className={cn(
                    "p-4 rounded-xl border transition-all cursor-pointer space-y-2 relative",
                    feePreference === "charge_me" ? "bg-primary/5 border-primary shadow-sm" : "bg-card border-border hover:bg-muted/40"
                  )}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-foreground">Absorb Fees (Merchant Pays)</span>
                    <div className={cn(
                      "h-4 w-4 rounded-full border flex items-center justify-center",
                      feePreference === "charge_me" ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                    )}>
                      {feePreference === "charge_me" && <Check className="h-3 w-3 stroke-[3]" />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Fees are deducted from your payout. Customers pay the exact invoice amount.
                  </p>
                </div>

                {/* Option 2: Charge Customer */}
                <div 
                  onClick={() => setFeePreference("charge_customer")}
                  className={cn(
                    "p-4 rounded-xl border transition-all cursor-pointer space-y-2 relative",
                    feePreference === "charge_customer" ? "bg-primary/5 border-primary shadow-sm" : "bg-card border-border hover:bg-muted/40"
                  )}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-foreground">Pass Fees to Customer</span>
                    <div className={cn(
                      "h-4 w-4 rounded-full border flex items-center justify-center",
                      feePreference === "charge_customer" ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                    )}>
                      {feePreference === "charge_customer" && <Check className="h-3 w-3 stroke-[3]" />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Fees are added to checkout total. You receive 100% net settlement.
                  </p>
                </div>

                {/* Option 3: Custom Percentage */}
                <div 
                  onClick={() => setFeePreference("charge_percentage")}
                  className={cn(
                    "p-4 rounded-xl border transition-all cursor-pointer space-y-2 relative",
                    feePreference === "charge_percentage" ? "bg-primary/5 border-primary shadow-sm" : "bg-card border-border hover:bg-muted/40"
                  )}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-foreground">Custom Split Percentage</span>
                    <div className={cn(
                      "h-4 w-4 rounded-full border flex items-center justify-center",
                      feePreference === "charge_percentage" ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                    )}>
                      {feePreference === "charge_percentage" && <Check className="h-3 w-3 stroke-[3]" />}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Split transaction fees dynamically between merchant and customer.
                  </p>
                </div>

              </div>

              {/* Split inputs if percentage selected */}
              {feePreference === "charge_percentage" && (
                <div className="p-4 rounded-xl bg-muted/20 border border-border grid md:grid-cols-2 gap-4 animate-in fade-in duration-300">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Percentage Charged to Merchant (%)</Label>
                    <Input 
                      type="number" 
                      min={0} 
                      max={100} 
                      value={merchantFeePercent}
                      onChange={(e) => setMerchantFeePercent(e.target.value === "" ? "" : Number(e.target.value))}
                      className="h-9 text-xs font-bold" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold">Percentage Charged to Customer (%)</Label>
                    <Input 
                      type="number" 
                      disabled 
                      value={merchantFeePercent === "" ? "" : 100 - Number(merchantFeePercent)}
                      className="h-9 text-xs font-bold bg-muted" 
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Gateways / Channels */}
          <Card className="border shadow-sm">
            <CardHeader className="py-4 px-5 border-b bg-muted/10">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                <CardTitle className="text-sm font-bold">Payment Gateway Channels</CardTitle>
              </div>
              <CardDescription className="text-[11px]">Enable or disable payment options presented on your checkout page.</CardDescription>
            </CardHeader>
            <CardContent className="p-5">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: "card" as const, title: "Card Payments", desc: "Visa, Mastercard & Verve", icon: CreditCard },
                  { key: "transfer" as const, title: "Bank Transfer (NUBAN)", desc: "Instant Virtual Account Payment", icon: Landmark },
                  { key: "opay" as const, title: "OPay Wallet Direct", desc: "Express OPay App Checkout", icon: Zap },
                  { key: "palmpay" as const, title: "PalmPay Transfer", desc: "Direct PalmPay Integration", icon: Smartphone },
                  { key: "mobile_money" as const, title: "Mobile Money", desc: "Telco Wallets", icon: Smartphone },
                  { key: "ussd" as const, title: "USSD Code Payment", desc: "Bank USSD Codes (*919#, *737#)", icon: Sliders },
                ].map((item) => (
                  <div key={item.key} className="p-3.5 rounded-xl border border-border bg-card flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <item.icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-foreground">{item.title}</p>
                        <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                    <Switch 
                      checked={channels[item.key]} 
                      onCheckedChange={() => toggleChannel(item.key)} 
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: SECURITY & APPROVALS */}
        <TabsContent value="security" className="space-y-6 m-0">
          <Card className="border shadow-sm">
            <CardHeader className="py-4 px-5 border-b bg-muted/10">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                <CardTitle className="text-sm font-bold">Security Controls & Payout Thresholds</CardTitle>
              </div>
              <CardDescription className="text-[11px]">Configure dual-authorization and login security.</CardDescription>
            </CardHeader>
            <CardContent className="p-5 space-y-6">
              
              {/* 2FA */}
              <div className="flex items-center justify-between border-b pb-4">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold text-foreground">Enforce Two-Factor Authentication (2FA)</Label>
                  <p className="text-[11px] text-muted-foreground">Require an Authenticator App (TOTP) code for all admin logins.</p>
                </div>
                <Switch defaultChecked />
              </div>

              {/* Payout Threshold Approval */}
              <div className="space-y-4 border-b pb-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs font-bold text-foreground">Payout Dual-Approval Queue</Label>
                    <p className="text-[11px] text-muted-foreground">Require a second admin approval for payouts exceeding threshold amount.</p>
                  </div>
                  <Switch checked={requireApproval} onCheckedChange={setRequireApproval} />
                </div>

                {requireApproval && (
                  <div className="p-4 rounded-xl bg-muted/20 border border-border space-y-3 animate-in fade-in duration-300">
                    <Label className="text-xs font-bold text-foreground">Approval Threshold (₦)</Label>
                    <div className="flex items-center gap-3 max-w-md">
                      <Input 
                        type="number" 
                        value={approvalThreshold} 
                        onChange={(e) => setApprovalThreshold(e.target.value)}
                        className="h-9 text-xs font-bold font-mono" 
                      />
                      <Button size="sm" className="h-9 px-4 text-xs font-bold">Save Threshold</Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground italic">
                      Any single transfer over ₦{Number(approvalThreshold || 0).toLocaleString()} will pause in the Payout Approvals queue until authorized by an owner.
                    </p>
                  </div>
                )}
              </div>

              {/* Transaction Notifications */}
              <div className="flex items-center justify-between border-b pb-4">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold text-foreground">Merchant Email Instant Transaction Receipts</Label>
                  <p className="text-[11px] text-muted-foreground">Receive instant email receipts for incoming and outgoing funds.</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold text-foreground">Customer Email Receipts</Label>
                  <p className="text-[11px] text-muted-foreground">Automatically send styled email receipts to customers upon successful checkout.</p>
                </div>
                <Switch defaultChecked />
              </div>

            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}

